import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { useTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';

import { map } from './Map';
import store, { devicesActions } from '../store';
import StatusView from './StatusView';

const SelectedDeviceMap = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const popupRef = useRef(null);
  const trackedDeviceIdRef = useRef(null);
  const cancelledRef = useRef(false);
  const placeholderRef = useRef(null);

  const selectedId = useSelector(state => state.devices.selectedId);
  const position = useSelector(state => {
    if (state.devices.selectedId) {
      return state.positions.items[state.devices.selectedId] || null;
    }
    return null;
  });
  const positions = useSelector(state => Object.values(state.positions));
  const zoomToId = useSelector(state => state.devices.zoomToId);
  const zoomToCounter = useSelector(state => state.devices.zoomToCounter);

  const cleanupPopup = () => {
    if (popupRef.current) {
      popupRef.current.off('close');
      if (placeholderRef.current) {
        ReactDOM.unmountComponentAtNode(placeholderRef.current);
      }
      popupRef.current.remove();
      popupRef.current = null;
    }
    if (placeholderRef.current) {
      placeholderRef.current = null;
    }
    trackedDeviceIdRef.current = null;
  };

  useEffect(() => {
    cancelledRef.current = false;

    if (!selectedId || !position) {
      cleanupPopup();
      return;
    }

    cleanupPopup();

    map.flyTo({
      center: [position.longitude, position.latitude],
      zoom: 15,
      essential: true,
    });

    const onMoveEnd = () => {
      if (cancelledRef.current) return;

      const hasOtherPopup = map.getContainer().querySelector('.mapboxgl-popup');
      if (hasOtherPopup) {
        return;
      }

      const placeholder = document.createElement('div');
      placeholderRef.current = placeholder;
      ReactDOM.render(
        <Provider store={store}>
          <ThemeProvider theme={theme}>
            <StatusView
              deviceId={selectedId}
              onShowDetails={positionId => history.push(`/position/${positionId}`)}
              history={history}
            />
          </ThemeProvider>
        </Provider>,
        placeholder
      );

      const popup = new mapboxgl.Popup({
        offset: 25,
        anchor: 'top',
      })
        .setDOMContent(placeholder)
        .setLngLat([position.longitude, position.latitude])
        .addTo(map);

      popup.on('close', () => {
        ReactDOM.unmountComponentAtNode(placeholder);
        popupRef.current = null;
        placeholderRef.current = null;
        trackedDeviceIdRef.current = null;
        dispatch(devicesActions.select({ id: null }));
      });

      popupRef.current = popup;
      trackedDeviceIdRef.current = selectedId;
    };

    map.once('moveend', onMoveEnd);

    return () => {
      cancelledRef.current = true;
      cleanupPopup();
    };
  }, [selectedId, position]);

  useEffect(() => {
    if (popupRef.current && trackedDeviceIdRef.current) {
      const pos = positions[0]?.[trackedDeviceIdRef.current];
      if (pos) {
        popupRef.current.setLngLat([pos.longitude, pos.latitude]);
      }
    }
  }, [positions]);

  useEffect(() => {
    if (zoomToId) {
      const pos = positions[0]?.[zoomToId];
      if (pos && map.getZoom() < 8) {
        map.flyTo({
          center: [pos.longitude, pos.latitude],
          zoom: 15,
          essential: true,
        });
      }
    }
  }, [zoomToId, zoomToCounter, positions]);

  return null;
};

export default SelectedDeviceMap;
